import { createContext, useContext, useState } from 'react'

import { useFvNavigate } from '@fv/client-components'
import { useQueryClient } from '@fv/client-core'
import type {
  ClientAddressLookupResult,
  FullShipment,
  ListShipment,
  ShipmentLocation,
} from '@fv/client-types'

import type { BookingFormContext, ReferrerType } from '@/features/booking/types'
import { defaultCustomerRoutedFormData } from '@/features/booking/types'
import {
  buildShipmentLocationFormModel,
  type ShipmentLocationFormModel,
} from '@/features/shipment-location-form/types'
import { useQueryParam } from '@/hooks/settings'
import { useStaticDataQuery } from '@/hooks/useStaticDataQuery'
import { routes } from '@/routes'
import type { AccountRecentCache } from '@/types/AccountRecentCache'
import { shipperFetch } from '@/utils/shipperFetch'

import { useAddresses } from '../addresses/useAddress'
import { useRole } from '../auth/hooks'
import { defaultFormBillTo } from '../billTo/billToFuncs'
import { defaultFormBroker } from '../customsBroker/brokerTypes'
import { defaultFormContact } from '../emergencyContact/contactFuncs'

export const bookingFormContext = createContext<BookingFormContext>({
  activateSection: () => null,
  billTo: defaultFormBillTo,
  bolNumber: '',
  setBolNumber: () => null,
  bookShipment: () => null,
  customsBrokers: [defaultFormBroker],
  customerRoutedData: defaultCustomerRoutedFormData,
  emergencyContact: defaultFormContact,
  formSections: [],
  goToNextSection: () => null,
  hasPickupOnlyPrompt: false,
  isBooking: false,
  isCustomerRouted: false,
  isPickupOnly: false,
  isRetender: false,
  loads: [],
  locations: [],
  onPickupOnlyChange: () => null,
  requireQuoteReason: false,
  selectedQuotes: [],
  setBillTo: () => null,
  setCustomerRoutedData: () => null,
  setCustomsBrokers: () => null,
  setEmergencyContact: () => null,
  setFormRef: () => null,
  setFormSections: () => null,
  setLoads: () => null,
  setLocations: () => null,
  settings: {
    requiresDeclaredValue: true,
    requiresFullBOLDetails: true,
    allowTLPostalCodeChange: false,
    requireCustomsBrokerValue: false,
    requireOriginEmail: false,
    requiresFlatbedDetails: false,
  },
  tags: [],
  setTags: () => null,
  glCode: '',
  setGLCode: () => null,
  brandId: undefined,
  setBrandId: () => null,
  shareEmails: '',
  setShareEmails: () => null,
})

export function useBookingForm() {
  return useContext(bookingFormContext)
}

export function toFormLocation(
  shipmentLocation: ShipmentLocation,
  load: Pick<FullShipment, 'shares' | 'workflow'>,
  addresses?: ClientAddressLookupResult[],
): ShipmentLocationFormModel {
  return buildShipmentLocationFormModel(shipmentLocation, load, addresses, {
    assignFormId: true,
  })
}

export function useLocations(
  loadsToBook: Array<Pick<FullShipment, 'locations' | 'shares' | 'workflow'>>,
) {
  const load = loadsToBook[0]
  const addressQuery = useAddresses(loadsToBook[0])
  const [locations, setLocations] = useState(() =>
    load.locations.map(l => toFormLocation(l, load, addressQuery.data)),
  )

  return {
    locations,
    setLocations,
  }
}

const fetchRecentAccountData = async (): Promise<AccountRecentCache> =>
  shipperFetch<AccountRecentCache>(`/accounts/settings/recent`)

const recentAccountDataKey = 'recent-account-data'

export const useRecentAccountData = (enabled = true) => {
  const role = useRole()
  return useStaticDataQuery([recentAccountDataKey], fetchRecentAccountData, {
    enabled: enabled && role === 'shipper',
  })
}

export const useMutateRecentAccountData = () => {
  const queryClient = useQueryClient()

  return {
    clear: () =>
      queryClient.invalidateQueries({ queryKey: [recentAccountDataKey] }),
  }
}

export const useBookingPageNavigation = () => {
  const navigate = useFvNavigate()
  const referrer = useQueryParam<ReferrerType>('ref')

  const leave = (
    loads: Pick<ListShipment, 'status' | 'loadId' | 'quoteRequestId'>[],
    returnToReferrer = false,
  ) => {
    // when editing (not creating) a multi truck load - loads.length = 1.
    // so shipper will be taken back to listing or details currently - expected?
    const firstLoad = loads[0]
    const { loadId, status, quoteRequestId } = firstLoad
    if (loads.length > 1 && quoteRequestId) {
      return navigate(`/search?text=${quoteRequestId}`)
    }

    if (!returnToReferrer || (returnToReferrer && referrer === 'details')) {
      return navigate(routes.details(loadId))
    }

    const isPending = status === 'pending' || status === 'awarded'
    const qs = `load=${loadId}`
    return navigate(isPending ? `/pending?${qs}` : `/active?${qs}`)
  }
  return {
    leave,
  }
}
